/**
 * fit-text — REGOLA GENERALE riusabile (portabile as-is su inglese e tool futuri).
 *
 * Rimpicciolisce il font di un elemento finché il suo contenuto ENTRA nella
 * propria box (niente overflow, niente scroll a mano) e ri-calcola in automatico
 * quando la box cambia dimensione (resize, rotazione, apertura/chiusura sidebar).
 * Pensato per box a dimensione VINCOLATA in cui il testo varia — es. la caption
 * "Racconta" su LIM, che deve mostrarsi INTERA in basso senza toccare con le mani.
 *
 * Comportamento chiave (non-regressivo):
 *   - a ogni refit AZZERA il font-size inline e rilegge la dimensione di DESIGN
 *     dal CSS (rispetta i token responsive / il root-font gigante di proiezione);
 *   - se il testo entra già a quella dimensione → non tocca nulla (no-op puro,
 *     es. su PC dove la box non è vincolata);
 *   - altrimenti scende a passi fino a farlo entrare (mai sotto `min`).
 *
 * Nessuna dipendenza dal tool: `import { fitText } from '@/core/fit-text'`.
 */

export interface FitTextOptions {
  /** px minimo sotto cui non scendere (default 12). */
  min?: number;
  /** px massimo; default = dimensione di design letta dal CSS a ogni refit. */
  max?: number;
  /** granularità della discesa in px (default 0.5). */
  step?: number;
}

export interface FitTextHandle {
  /** Ricalcola subito (utile dopo un cambio di contenuto). */
  refit(): void;
  /** Stacca l'osservatore e ripulisce lo stile inline. */
  disconnect(): void;
}

export function fitText(el: HTMLElement, options: FitTextOptions = {}): FitTextHandle {
  const min = options.min ?? 12;
  const step = options.step ?? 0.5;

  const fits = (): boolean =>
    el.scrollHeight <= el.clientHeight + 1 && el.scrollWidth <= el.clientWidth + 1;

  const refit = (): void => {
    el.style.fontSize = ''; // torna alla dimensione di DESIGN (CSS / var responsive)
    const designMax =
      options.max ?? (parseFloat(getComputedStyle(el).fontSize) || 16);
    if (fits()) return; // entra già: niente da fare (no-op su box non vincolate)

    let size = designMax;
    let guard = 0;
    while (!fits() && size > min && guard < 600) {
      size = Math.max(min, size - step);
      el.style.fontSize = `${size}px`;
      guard += 1;
    }
  };

  let raf = 0;
  const schedule = (): void => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      refit();
    });
  };

  const ro =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
  ro?.observe(el);
  refit();

  return {
    refit,
    disconnect: () => {
      ro?.disconnect();
      if (raf) cancelAnimationFrame(raf);
      el.style.fontSize = '';
    },
  };
}
